import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkplaceNameDisplay from '../../components/WorkplaceNameDisplay';
import { workplaceApi, documentToolsApi } from '../../services/api';
import './AnnualWorkPlanPage.css';

interface Workplace {
  id: number;
  title?: string;
  name?: string;
  address?: string;
  dangerClass?: 'low' | 'dangerous' | 'veryDangerous';
  riskLevel?: 'low' | 'dangerous' | 'veryDangerous' | 'very_dangerous';
  sskRegistrationNo?: string;
  sgkNo?: string;
  city?: string;
  district?: string;
  employeeCount?: number;
  contactPerson?: string;
}

interface CategoryItem {
  id: string;
  name: string;
  period?: string;
  defaultMonths?: number[];
}

interface Category {
  id: string;
  title: string;
  items: CategoryItem[];
}

interface PlanData {
  year: number;
  workplace: {
    id: number;
    name: string;
    address: string;
    riskLevel: string;
    sgkNo: string;
  };
  expert: {
    id: number;
    name: string;
    certificateNo: string;
  } | null;
  doctor: {
    id: number;
    name: string;
    diplomaNo: string;
  } | null;
  employer: {
    name: string;
    title: string;
  };
  categories: Category[];
  matrix: Record<string, Record<number, boolean>>;
}

const RISK_LEVEL_LABEL: Record<string, string> = {
  low: 'Az Tehlikeli',
  dangerous: 'Tehlikeli',
  veryDangerous: 'Çok Tehlikeli',
  very_dangerous: 'Çok Tehlikeli'
};

const MONTH_NAMES = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const normalizeSearch = (s: string) =>
  (s || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/i̇/g, 'i')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim();

export const AnnualWorkPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<number>(1);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [selectedWp, setSelectedWp] = useState<Workplace | null>(null);

  // Plan Data & Matrix State
  const [planLoading, setPlanLoading] = useState<boolean>(false);
  const [plan, setPlan] = useState<PlanData | null>(null);
  const [matrix, setMatrix] = useState<Record<string, Record<number, boolean>>>({});
  const [employerName, setEmployerName] = useState<string>('');
  const [employerTitle, setEmployerTitle] = useState<string>('İşveren / İşveren Vekili');
  const [downloading, setDownloading] = useState<boolean>(false);

  // Inline Consent States
  const [wordConsentOpen, setWordConsentOpen] = useState<boolean>(false);
  const [wordConsentChecked, setWordConsentChecked] = useState<boolean>(false);

  const [pdfConsentOpen, setPdfConsentOpen] = useState<boolean>(false);
  const [pdfConsentChecked, setPdfConsentChecked] = useState<boolean>(false);

  const scrollToTop = () => {
    const body = document.querySelector('.awp-body') as HTMLElement | null;
    if (body) {
      body.scrollTop = 0;
    } else {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [step]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchWorkplaces();
  }, []);

  const fetchWorkplaces = async () => {
    try {
      setLoading(true);
      const res = await workplaceApi.getAll();
      const list = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);

      const normalized: Workplace[] = list.map((wp: any) => ({
        id: wp.id,
        name: wp.title || wp.name || `İşyeri #${wp.id}`,
        title: wp.title || wp.name,
        address: wp.address || [wp.district, wp.city].filter(Boolean).join(' / ') || '',
        riskLevel: wp.dangerClass || wp.riskLevel || 'dangerous',
        dangerClass: wp.dangerClass || wp.riskLevel || 'dangerous',
        sgkNo: wp.sgkNo || wp.sskRegistrationNo || '',
        sskRegistrationNo: wp.sgkNo || wp.sskRegistrationNo || '',
        city: wp.city,
        district: wp.district,
        employeeCount: wp.employeeCount,
        contactPerson: wp.contactPerson
      }));

      setWorkplaces(normalized);
    } catch (err) {
      console.error('Workplaces fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkplaces = useMemo(() => {
    const q = normalizeSearch(searchQuery);
    if (!q) return workplaces;
    return workplaces.filter(wp => {
      const nameStr = normalizeSearch(wp.name || wp.title || '');
      const sgkStr = normalizeSearch(wp.sskRegistrationNo || wp.sgkNo || '');
      const cityStr = normalizeSearch(wp.city || '');
      const distStr = normalizeSearch(wp.district || '');
      return nameStr.includes(q) || sgkStr.includes(q) || cityStr.includes(q) || distStr.includes(q);
    });
  }, [workplaces, searchQuery]);

  const handleSelectWorkplace = (wp: Workplace) => {
    setSelectedWp(wp);
    setSearchQuery(wp.name || wp.title || '');
    setDropdownOpen(false);
  };

  const loadPlanForWorkplace = async (wpId: number) => {
    try {
      setPlanLoading(true);
      const data: PlanData = await documentToolsApi.getAnnualPlan(wpId);
      if (data) {
        setPlan(data);
        setMatrix(data.matrix || {});
        setEmployerName(data.employer?.name || selectedWp?.contactPerson || '');
        setEmployerTitle(data.employer?.title || 'İşveren / İşveren Vekili');
      }
    } catch (err) {
      console.error('Error loading plan:', err);
    } finally {
      setPlanLoading(false);
    }
  };

  const goToStep2 = () => {
    if (!selectedWp) {
      alert('Lütfen bir işyeri seçiniz.');
      return;
    }
    loadPlanForWorkplace(selectedWp.id);
    setStep(2);
    scrollToTop();
  };

  const toggleMonth = (itemId: string, month: number) => {
    setMatrix(prev => {
      const itemState = { ...(prev[itemId] || {}) };
      itemState[month] = !itemState[month];
      return { ...prev, [itemId]: itemState };
    });
  };

  const executeWordDownload = async () => {
    if (!wordConsentChecked) {
      alert('Devam etmek için lütfen İSG mevzuatı onay kutusunu işaretleyiniz.');
      return;
    }

    if (!selectedWp) return;

    try {
      setDownloading(true);
      const year = plan?.year || new Date().getFullYear();
      const safeName = (selectedWp.name || selectedWp.title || 'Isyeri').replace(/[^a-zA-Z0-9_\-]/g, '_');
      const filename = `Yillik_Calisma_Plani_${year}_${safeName}.docx`;

      await documentToolsApi.downloadAnnualPlanDocx(selectedWp.id, year, matrix, filename);
    } catch (err: any) {
      console.error('Failed to download docx:', err);
      alert('❌ Word belgesi indirilirken hata oluştu. Lütfen tekrar deneyiniz.');
    } finally {
      setDownloading(false);
    }
  };

  const executePrintPdf = () => {
    if (!pdfConsentChecked) {
      alert('Devam etmek için lütfen İSG mevzuatı onay kutusunu işaretleyiniz.');
      return;
    }
    window.print();
  };

  const wpName = selectedWp?.name || selectedWp?.title || '';
  const wpRisk = (selectedWp?.riskLevel || selectedWp?.dangerClass || 'dangerous') as string;

  return (
    <div className="awp-page">
      {/* Top Header */}
      <div className="awp-header">
        <div className="awp-header-left">
          <span className="awp-header-icon">📅</span>
          <div>
            <h1 className="awp-title">Yıllık Çalışma Planı</h1>
            <p className="awp-subtitle">6331 Sayılı İSG Kanunu Madde 8 Kapsamında Resmi Yıllık Çalışma Planı Üretici</p>
          </div>
        </div>
        <button
          className="awp-btn-secondary"
          style={{ padding: '6px 14px', fontSize: '12px' }}
          onClick={() => navigate('/documents')}
        >
          ← Belge Yönetimine Dön
        </button>
      </div>

      {/* Stepper Bar */}
      <div className="awp-stepper">
        {[
          { num: 1, title: 'İşyeri Seçimi' },
          { num: 2, title: 'Çalışma Matrisi' },
          { num: 3, title: 'Belge İndir & PDF' }
        ].map((s) => (
          <div
            key={s.num}
            onClick={() => {
              if (s.num === 1) { setStep(1); scrollToTop(); }
              if (s.num === 2 && selectedWp) { setStep(2); scrollToTop(); }
              if (s.num === 3 && selectedWp) { setStep(3); scrollToTop(); }
            }}
            className={`awp-step ${step === s.num ? 'active' : ''} ${step > s.num ? 'done' : ''}`}
          >
            <div className="awp-step-num">{step > s.num ? '✓' : s.num}</div>
            <div className="awp-step-label">{s.title}</div>
          </div>
        ))}
      </div>

      {/* Body Area */}
      <div className="awp-body">
        {/* STEP 1: İşyeri Seçimi */}
        {step === 1 && (
          <div className="flex-1 overflow-y-auto">
            <div className="awp-card">
              <h2 className="awp-card-title">İşyeri Seçin</h2>
              <p className="text-xs text-slate-500 mb-4">
                İşyeri ünvanı veya SGK Sicil No girerek arama yapabilirsiniz:
              </p>

              {loading ? (
                <div className="py-8 text-center text-slate-500 text-sm">İşyerleri yükleniyor...</div>
              ) : (
                <div className="awp-wp-search-wrap" ref={searchWrapRef}>
                  <div className="awp-wp-search-box">
                    <span className="awp-wp-search-icon">🔍</span>
                    <input
                      type="text"
                      className="awp-wp-search-input"
                      placeholder="İşyeri adı veya SGK Sicil No ile arayın..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setDropdownOpen(true);
                      }}
                      onFocus={(e) => {
                        e.target.select();
                        setDropdownOpen(true);
                      }}
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="awp-wp-search-clear"
                        title="Temizle"
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedWp(null);
                          setDropdownOpen(true);
                        }}
                      >
                        ✕
                      </button>
                    )}
                    <button
                      type="button"
                      className="awp-wp-search-toggle"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: '#64748b', fontSize: '13px' }}
                      title="İşyeri Listesini Aç / Kapat"
                      onClick={() => setDropdownOpen(prev => !prev)}
                    >
                      ▾
                    </button>
                  </div>

                  {/* Dropdown Results */}
                  {dropdownOpen && (
                    <div className="awp-wp-dropdown">
                      {filteredWorkplaces.length === 0 ? (
                        <div className="awp-wp-dropdown-empty">Eşleşen işyeri bulunamadı.</div>
                      ) : (
                        filteredWorkplaces.map((wp) => (
                          <div
                            key={wp.id}
                            className={`awp-wp-dropdown-item ${selectedWp?.id === wp.id ? 'active' : ''}`}
                            onClick={() => handleSelectWorkplace(wp)}
                          >
                            <div className="awp-wp-dropdown-main">
                              <span className="awp-wp-dropdown-name">
                                <WorkplaceNameDisplay name={wp.name || wp.title} />
                              </span>
                              <span className={`awp-risk-badge awp-risk-${wp.riskLevel || wp.dangerClass}`}>
                                {RISK_LEVEL_LABEL[wp.riskLevel || wp.dangerClass || 'dangerous']}
                              </span>
                            </div>
                            <div className="awp-wp-dropdown-sub">
                              <span>SGK: <strong>{wp.sskRegistrationNo || wp.sgkNo || '—'}</strong></span>
                              {(wp.city || wp.district) && <span>{[wp.city, wp.district].filter(Boolean).join(' / ')}</span>}
                              {wp.employeeCount && <span>👥 {wp.employeeCount} Çalışan</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Selected Workplace Preview */}
                  {selectedWp && (
                    <div className="awp-wp-preview">
                      <div className="awp-wp-preview-grid">
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">Seçilen Firma</span>
                          <span className="awp-wp-preview-val">{wpName}</span>
                        </div>
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">SGK Sicil No</span>
                          <span className="awp-wp-preview-val font-mono">{selectedWp.sskRegistrationNo || selectedWp.sgkNo || '—'}</span>
                        </div>
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">Tehlike Sınıfı</span>
                          <div>
                            <span className={`awp-risk-badge awp-risk-${wpRisk}`}>
                              {RISK_LEVEL_LABEL[wpRisk] || wpRisk}
                            </span>
                          </div>
                        </div>
                        <div className="awp-wp-preview-item">
                          <span className="awp-wp-preview-label">Adres</span>
                          <span className="awp-wp-preview-val text-xs">{selectedWp.address || 'Adres bilgisi kayıtlı değil'}</span>
                        </div>
                      </div>

                      <button
                        onClick={goToStep2}
                        className="awp-btn-continue"
                      >
                        Devam Et (Çalışma Matrisi) →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 2: Çalışma Matrisi */}
        {step === 2 && selectedWp && (
          <div className="space-y-2">
            <div className="awp-card" style={{ padding: '10px 14px' }}>
              {/* Üst Bilgi Satırı */}
              <div className="flex justify-between items-center flex-wrap gap-2 pb-2 border-b">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">FİRMA:</span>
                  <h2 className="text-sm font-black text-slate-900 leading-none">{wpName}</h2>
                  <span className={`awp-risk-badge awp-risk-${wpRisk}`} style={{ fontSize: '10px', padding: '2px 8px', lineHeight: 1.2 }}>
                    {RISK_LEVEL_LABEL[wpRisk] || wpRisk}
                  </span>
                  <span className="text-[11px] text-slate-500 font-mono">SGK: <strong>{selectedWp.sskRegistrationNo || selectedWp.sgkNo || '—'}</strong></span>
                </div>

                <div className="text-[11px] text-blue-900 bg-blue-50 border border-blue-200 py-1 px-3 rounded flex items-center gap-1.5 font-medium">
                  <span>💡</span>
                  <span>Hücrelere tıklayarak <strong className="text-red-700 font-black">X</strong> seçimi yapabilirsiniz</span>
                </div>
              </div>

              {planLoading ? (
                <div className="p-8 text-center text-slate-500 text-xs">Çalışma matrisi yükleniyor...</div>
              ) : (
                <div className="mt-2 space-y-2">
                  <div className="awp-matrix-table-container">
                    <table className="w-full text-left" style={{ minWidth: '720px' }}>
                      <thead>
                        <tr className="bg-slate-800 text-white font-semibold">
                          <th className="py-1 px-2 border border-slate-700 min-w-[240px] text-[10px]">YAPILACAK ÇALIŞMA / FAALİYET</th>
                          <th className="py-1 px-1 border border-slate-700 text-center w-14 text-[9.5px]">PERİYOT</th>
                          {MONTH_NAMES.map((m, idx) => (
                            <th key={idx} className="py-1 px-0.5 border border-slate-700 text-center min-w-[28px] w-7 font-mono text-[9.5px] leading-tight">
                              <span>{m.slice(0, 3)}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {plan?.categories.map((category) => (
                          <React.Fragment key={category.id}>
                            <tr className="bg-blue-100/80 text-blue-900 font-bold border-t-2 border-blue-300">
                              <td colSpan={14} className="py-1 px-2 tracking-wide uppercase text-[10px] bg-blue-100">
                                <strong style={{ fontWeight: 900, color: '#1e3a5f' }}>{category.title}</strong>
                              </td>
                            </tr>
                            {category.items.map((item) => {
                              const itemMatrix = matrix[item.id] || {};
                              return (
                                <tr key={item.id} className="hover:bg-slate-50 transition border-b border-slate-200">
                                  <td className="py-1 px-2 text-slate-800 font-medium border-r border-slate-200 text-[10.5px] leading-tight">
                                    {item.name}
                                  </td>
                                  <td className="py-1 px-1 text-center text-slate-500 border-r border-slate-200 text-[10px]">
                                    {item.period || '-'}
                                  </td>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                                    const checked = !!itemMatrix[m];
                                    return (
                                      <td
                                        key={m}
                                        onClick={() => toggleMonth(item.id, m)}
                                        className={`p-0.5 text-center cursor-pointer border-r border-slate-200 transition select-none ${
                                          checked ? 'bg-red-50 font-bold' : 'hover:bg-blue-50'
                                        }`}
                                      >
                                        {checked ? (
                                          <span className="inline-flex items-center justify-center w-4 h-4 rounded font-black" style={{ color: '#b91c1c', fontWeight: 900, fontSize: '13px', lineHeight: 1 }}>
                                            X
                                          </span>
                                        ) : (
                                          <span className="text-slate-300 font-bold hover:text-blue-600 transition text-[9px]">•</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Buton Grubu */}
                  <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(1);
                        scrollToTop();
                      }}
                      className="awp-btn-danger"
                      style={{ flex: '1 1 0%', height: '40px' }}
                    >
                      <span>←</span> İşyeri Seçimi (Geri)
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setStep(3);
                        scrollToTop();
                      }}
                      className="awp-btn-generate"
                      style={{ flex: '1 1 0%', height: '40px' }}
                    >
                      Devam Et (Belge İndir & PDF) <span>→</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3: Belge İndir & PDF */}
        {step === 3 && selectedWp && (
          <div className="awp-step3-wrapper flex-1 overflow-y-auto">
            <div className="awp-card awp-step3-card">
              {/* Başlık Satırı */}
              <div className="awp-step3-header">
                <div>
                  <span className="awp-step3-firm-label">SEÇİLEN FİRMA</span>
                  <h2 className="awp-step3-firm-name">{wpName}</h2>
                </div>
                <span className="awp-step3-ready-badge">✓ Plan Hazır</span>
              </div>

              {/* Açıklama + İki Buton Yan Yana */}
              <div className="awp-step3-desc-row">
                <p className="awp-step3-desc">
                  Yıllık Çalışma Planı belgesini <strong>Word (.docx)</strong> veya
                  <strong> PDF / Yazdır</strong> formatında oluşturun.
                  Belge resmi başlıklar, periyot matrisi ve imza alanlarını içerir.
                  İndirme sonrası ıslak imza / kaşe ile tamamlanmalıdır.
                </p>
                <div className="awp-step3-btn-row">
                  <button
                    onClick={() => { setWordConsentOpen(!wordConsentOpen); setPdfConsentOpen(false); }}
                    disabled={downloading}
                    className="awp-btn-save awp-step3-btn"
                  >
                    📥 Word (.docx)
                  </button>
                  <button
                    onClick={() => { setPdfConsentOpen(!pdfConsentOpen); setWordConsentOpen(false); }}
                    className="awp-btn-generate awp-step3-btn"
                  >
                    🖨️ PDF / Yazdır
                  </button>
                </div>
              </div>

              {/* Word Onay Paneli */}
              {wordConsentOpen && (
                <div className="awp-consent-panel awp-consent-word">
                  <div className="awp-consent-title">
                    <span className="awp-consent-icon">⚖️</span>
                    <div>
                      <strong>Beyan ve Onam — Word İndirme</strong>
                      <p>6331 Sayılı İSG Kanunu Madde 8 kapsamında planın mevzuata uygun olduğunu beyan ederim.</p>
                    </div>
                  </div>
                  <label className="awp-consent-check">
                    <input
                      type="checkbox"
                      checked={wordConsentChecked}
                      onChange={(e) => setWordConsentChecked(e.target.checked)}
                    />
                    <span>Beyanı okudum, mesleki sorumlulukları kabul ediyorum.</span>
                  </label>
                  <div className="awp-consent-actions">
                    <button onClick={() => setWordConsentOpen(false)} className="awp-consent-cancel">Vazgeç</button>
                    <button
                      onClick={executeWordDownload}
                      disabled={!wordConsentChecked || downloading}
                      className={`awp-consent-confirm awp-consent-confirm-green ${!wordConsentChecked ? 'disabled' : ''}`}
                    >
                      📥 {downloading ? 'İndiriliyor...' : 'Onayla ve İndir'}
                    </button>
                  </div>
                </div>
              )}

              {/* PDF Onay Paneli */}
              {pdfConsentOpen && (
                <div className="awp-consent-panel awp-consent-pdf">
                  <div className="awp-consent-title">
                    <span className="awp-consent-icon">⚖️</span>
                    <div>
                      <strong>Beyan ve Onam — PDF / Yazdır</strong>
                      <p>6331 Sayılı İSG Kanunu Madde 8 kapsamında planın mevzuata uygun olduğunu beyan ederim.</p>
                    </div>
                  </div>
                  <label className="awp-consent-check">
                    <input
                      type="checkbox"
                      checked={pdfConsentChecked}
                      onChange={(e) => setPdfConsentChecked(e.target.checked)}
                    />
                    <span>Beyanı okudum, mesleki sorumlulukları kabul ediyorum.</span>
                  </label>
                  <div className="awp-consent-actions">
                    <button onClick={() => setPdfConsentOpen(false)} className="awp-consent-cancel">Vazgeç</button>
                    <button
                      onClick={executePrintPdf}
                      disabled={!pdfConsentChecked}
                      className={`awp-consent-confirm awp-consent-confirm-blue ${!pdfConsentChecked ? 'disabled' : ''}`}
                    >
                      🖨️ Onayla ve Yazdır
                    </button>
                  </div>
                </div>
              )}

              {/* İmza Blokları */}
              <div className="awp-step3-sig-section">
                <h4 className="awp-step3-sig-title">BELGE İMZA VE KAŞE DETAYLARI</h4>
                <div className="awp-step3-sig-grid">
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">HAZIRLAYAN (UZMAN)</p>
                    <p className="awp-step3-sig-name">{plan?.expert ? plan.expert.name : 'İş Güvenliği Uzmanı'}</p>
                  </div>
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">ONAYLAYAN (İŞVEREN)</p>
                    <p className="awp-step3-sig-name">{employerName || selectedWp.contactPerson || 'İşveren / İşveren Vekili'}</p>
                  </div>
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">KONTROL (HEKİM)</p>
                    <p className="awp-step3-sig-name">{plan?.doctor ? plan.doctor.name : 'İşyeri Hekimi'}</p>
                  </div>
                </div>
              </div>

              {/* Geri Butonu */}
              <div className="awp-step3-back-row">
                <button
                  type="button"
                  onClick={() => { setStep(2); scrollToTop(); }}
                  className="awp-btn-danger"
                  style={{ padding: '8px 18px', fontSize: '13px' }}
                >
                  ← Çalışma Matrisine Dön
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnualWorkPlanPage;
